// use-wallet-monitor — 60s-Poll der getrackten Wallets via ClearinghouseState.
// V1-Pendant: src/hooks/useWalletMonitor.js.
//
// Verantwortungen:
// 1. Wallet-Liste aus `/eve/wallets` laden.
// 2. CRUD auf getrackten Wallets (add/remove/update).
//
// V3-Refactor: tests können `pollIntervalMs` setzen und initial-poll skippen.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  addWatchedWallet,
  deleteWatchedWallet,
  listWatchedWallets,
  updateWatchedWallet,
  type AddWalletInput,
  type UpdateWalletInput,
} from '@/lib/wallet-api';
import { WALLET_POLL_INTERVAL_MS } from '@/lib/wallet-monitor';
import { selectAccessToken, useAuthStore } from '@/stores/auth-store';
import type { WatchedWallet } from '@/types/wallet-monitor';

export interface UseWalletMonitorOptions {
  /** Override Polling-Interval (Test-Hilfe). */
  pollIntervalMs?: number;
  /** Test-Hilfe: kein Auto-Mount-Poll. */
  enabled?: boolean;
}

export interface UseWalletMonitorReturn {
  wallets: WatchedWallet[];
  isWalletTracked: (address: string) => boolean;
  reloadWallets: () => Promise<void>;
  pollNow: () => Promise<void>;
  /** Wallet hinzufügen (POST + reload). Wirft bei Conflict (409) oder
   *  ungültiger Adresse — Caller zeigt Error-Toast. */
  addWallet: (input: AddWalletInput) => Promise<WatchedWallet>;
  /** Wallet löschen (DELETE + reload). */
  removeWallet: (id: string) => Promise<void>;
  /** Label / Threshold updaten (PATCH + reload). */
  updateWallet: (id: string, patch: UpdateWalletInput) => Promise<WatchedWallet>;
}

export function useWalletMonitor(options: UseWalletMonitorOptions = {}): UseWalletMonitorReturn {
  const { pollIntervalMs = WALLET_POLL_INTERVAL_MS, enabled = true } = options;
  const accessToken = useAuthStore(selectAccessToken);

  const [wallets, setWallets] = useState<WatchedWallet[]>([]);
  const walletsRef = useRef<WatchedWallet[]>([]);

  const refreshWallets = useCallback(async (): Promise<WatchedWallet[]> => {
    if (!accessToken) return [];
    try {
      const list = await listWatchedWallets(accessToken);
      walletsRef.current = list;
      setWallets(list);
      return list;
    } catch (e) {
      console.warn('[wallet-monitor] reload failed:', e instanceof Error ? e.message : e);
      return walletsRef.current;
    }
  }, [accessToken]);

  const pollNow = useCallback(async (): Promise<void> => {
    if (!accessToken) return;
    await refreshWallets();
  }, [accessToken, refreshWallets]);

  useEffect(() => {
    if (!enabled || !accessToken) return undefined;
    void pollNow();
    const id = setInterval(() => {
      void pollNow();
    }, pollIntervalMs);
    return () => clearInterval(id);
  }, [enabled, accessToken, pollNow, pollIntervalMs]);

  const isWalletTracked = useCallback(
    (address: string): boolean => walletsRef.current.some((w) => w.address === address),
    [],
  );

  const reloadWallets = useCallback(async (): Promise<void> => {
    await refreshWallets();
  }, [refreshWallets]);

  const addWallet = useCallback(
    async (input: AddWalletInput): Promise<WatchedWallet> => {
      if (!accessToken) throw new Error('Nicht eingeloggt');
      const created = await addWatchedWallet(input, accessToken);
      await refreshWallets();
      return created;
    },
    [accessToken, refreshWallets],
  );

  const removeWallet = useCallback(
    async (id: string): Promise<void> => {
      if (!accessToken) throw new Error('Nicht eingeloggt');
      await deleteWatchedWallet(id, accessToken);
      await refreshWallets();
    },
    [accessToken, refreshWallets],
  );

  const updateWallet = useCallback(
    async (id: string, patch: UpdateWalletInput): Promise<WatchedWallet> => {
      if (!accessToken) throw new Error('Nicht eingeloggt');
      const updated = await updateWatchedWallet(id, patch, accessToken);
      await refreshWallets();
      return updated;
    },
    [accessToken, refreshWallets],
  );

  return { wallets, isWalletTracked, reloadWallets, pollNow, addWallet, removeWallet, updateWallet };
}
